/**
 * Staggered regional audit schedule admin.
 *
 * Each slot audits a set of states on its own cron at its own window. Spread
 * across the day, each run stays small enough for a 24-48h window — which is
 * what turns encroachment into a next-day notification (7/20 meeting).
 *
 * Two switches gate firing: the env-level master switch (shown read-only here)
 * and each slot's own toggle. "Run now" works regardless, so a slot can be
 * trialled before it's ever armed.
 */

import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { api, ApiError } from '../lib/api';
import { SectionHeading, useSectionOpen } from './SectionToggle';

interface Slot {
  id: number;
  name: string;
  scope_states: string[];
  group_id: number | null;
  group_name: string | null;
  group_kind: 'states' | 'afs' | null;
  group_members: string[] | null;
  cron_expression: string;
  timezone: string;
  audit_window_days: number;
  sync_zoho: boolean;
  sync_axiscare: boolean;
  enabled: boolean;
  armed: boolean;
  last_run_at: string | null;
  last_job_id: number | null;
  last_job_status: string | null;
  last_skip_reason: string | null;
}

const TIMEZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York', 'America/Phoenix', 'UTC',
];

export function AuditSchedules() {
  const [open, toggle] = useSectionOpen('schedules');
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [schedulerEnabled, setSchedulerEnabled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const load = () => {
    api<{ schedules: Slot[]; scheduler_enabled: boolean }>('/api/admin/schedules')
      .then((d) => { setSlots(d.schedules); setSchedulerEnabled(d.scheduler_enabled); })
      .catch((e) => setError(e instanceof ApiError && e.status === 403
        ? 'Admin role required to manage schedules.'
        : (e instanceof Error ? e.message : 'load failed')));
  };
  useEffect(load, []);

  const patch = async (id: number, body: Record<string, unknown>) => {
    setBusy(id); setError(null);
    try {
      await api(`/api/admin/schedules/${id}`, { method: 'PUT', body });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally { setBusy(null); }
  };

  const runNow = async (s: Slot) => {
    if (!confirm(`Queue an audit for "${s.name}" now?\n\n${s.group_id != null ? `Group: ${(s.group_members ?? []).join(', ')}` : `States: ${s.scope_states.join(', ') || '(enabled states)'}`}\nWindow: last ${s.audit_window_days} day(s)`)) return;
    setBusy(s.id); setError(null);
    try {
      const r = await api<{ jobId: number }>(`/api/admin/schedules/${s.id}/run`, { method: 'POST' });
      alert(`Queued audit job #${r.jobId}. Watch it on the Audit runs screen.`);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'run failed');
    } finally { setBusy(null); }
  };

  const remove = async (s: Slot) => {
    if (!confirm(`Delete the "${s.name}" schedule slot?`)) return;
    setBusy(s.id);
    try { await api(`/api/admin/schedules/${s.id}`, { method: 'DELETE' }); load(); }
    catch (e) { setError(e instanceof Error ? e.message : 'delete failed'); }
    finally { setBusy(null); }
  };

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Admin</div>
        <SectionHeading open={open} onToggle={toggle}>Scheduled audits</SectionHeading>
        {open && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
          Each slot audits its states on its own schedule. Staggering regions across the day keeps each
          run small enough for a 24–48 hour window, so encroachment surfaces the next morning instead of
          in a monthly sweep.
        </div>}
      </div>
      {open && <>

      <div className="card card-pad" style={{
        marginBottom: 12, fontSize: 12.5,
        borderLeft: `3px solid var(--${schedulerEnabled ? 'ok' : 'warn'})`,
      }}>
        <b>Master switch: {schedulerEnabled ? 'ON' : 'OFF'}</b>
        {schedulerEnabled
          ? ' — enabled slots below will fire on their schedule.'
          : ' — no slot will fire on its own, whatever its toggle says. “Run now” still works for testing. Set NIGHTLY_SCHEDULER_ENABLED=true on the server to arm it.'}
      </div>

      {error && <div className="card card-pad c-danger" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {!slots && !error && <div className="card card-pad muted" style={{ fontSize: 13 }}>Loading…</div>}

      {slots?.map((s) => (
        <div key={s.id} className="card" style={{ padding: 0, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
            <Icon name="clock" size={14} />
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{s.name}</span>
            <span className="mono" style={{ fontSize: 12 }}>
              {s.group_id != null
                ? `Saved group · ${(s.group_members ?? []).join(', ') || '(empty)'}`
                : (s.scope_states.join(', ') || '(all enabled states)')}
            </span>
            {s.enabled
              ? (s.armed
                ? <span className="pill" style={{ height: 20, fontSize: 10.5 }}><span className="pill-dot dot-ok"></span>ARMED</span>
                : <span className="pill" style={{ height: 20, fontSize: 10.5 }}><span className="pill-dot dot-warn"></span>ENABLED · MASTER OFF</span>)
              : <span className="pill" style={{ height: 20, fontSize: 10.5 }}><span className="pill-dot dot-warn"></span>OFF</span>}
            <div style={{ flex: 1 }} />
            <button className="btn btn-sm btn-ghost" disabled={busy === s.id} onClick={() => runNow(s)} title="Queue this slot's audit immediately">
              <Icon name="play" size={12} /> Run now
            </button>
            <button
              className="btn btn-sm"
              disabled={busy === s.id}
              onClick={() => patch(s.id, { enabled: !s.enabled })}
              style={s.enabled ? undefined : { background: 'var(--accent)', color: '#fff' }}
            >
              {s.enabled ? 'Disable' : 'Enable'}
            </button>
            <button className="btn btn-sm btn-ghost btn-icon" disabled={busy === s.id} onClick={() => remove(s)} title="Delete slot">
              <Icon name="close" size={13} />
            </button>
          </div>

          {s.group_id != null ? (
            <div style={{ padding: '12px 16px', fontSize: 12.5 }} className="muted">
              Schedule for the saved group “{s.group_name}”. Change its locations or timing on the Trigger audit screen
              (<span className="mono">{s.cron_expression}</span>, {s.timezone}, last {s.audit_window_days} day(s)).
            </div>
          ) : (
          <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, alignItems: 'end' }}>
            <Field label="Cron (min hr dom mon dow)">
              <input className="input mono" style={{ fontSize: 12 }} defaultValue={s.cron_expression}
                onBlur={(e) => e.target.value !== s.cron_expression && patch(s.id, { cron_expression: e.target.value })} />
            </Field>
            <Field label="Timezone">
              <select className="select" style={{ fontSize: 12.5 }} value={s.timezone}
                onChange={(e) => patch(s.id, { timezone: e.target.value })}>
                {[...new Set([s.timezone, ...TIMEZONES])].map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </Field>
            <Field label="Window (days back)">
              <select className="select" style={{ fontSize: 12.5 }} value={String(s.audit_window_days)}
                onChange={(e) => patch(s.id, { audit_window_days: Number(e.target.value) })}>
                <option value="1">1 (last 24h)</option>
                <option value="2">2 (last 48h)</option>
                <option value="7">7</option>
                <option value="30">30</option>
                <option value="90">90 (spec default)</option>
              </select>
            </Field>
            <Field label="States (comma-separated)">
              <input className="input mono" style={{ fontSize: 12 }} defaultValue={s.scope_states.join(', ')}
                placeholder="blank = all enabled states"
                onBlur={(e) => {
                  const next = e.target.value.split(',').map((x) => x.trim().toUpperCase()).filter(Boolean);
                  if (next.join(',') !== s.scope_states.join(',')) patch(s.id, { scope_states: next });
                }} />
            </Field>
          </div>
          )}

          {(s.last_run_at || s.last_skip_reason) && (
            <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', fontSize: 11.5 }} className="muted">
              {s.last_run_at && <>Last run {new Date(s.last_run_at).toLocaleString()}
                {s.last_job_id ? ` · job #${s.last_job_id}` : ''}{s.last_job_status ? ` (${s.last_job_status})` : ''}</>}
              {s.last_skip_reason && <span className="c-warn"> · Last attempt skipped: {s.last_skip_reason}</span>}
            </div>
          )}
        </div>
      ))}

      {slots && (adding
        ? <NewSlot onDone={() => { setAdding(false); load(); }} onCancel={() => setAdding(false)} />
        : <button className="btn btn-sm" onClick={() => setAdding(true)}><Icon name="plus" size={12} /> Add schedule slot</button>
      )}
      </>}
    </div>
  );
}

function NewSlot({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [states, setStates] = useState('');
  const [cronExpr, setCronExpr] = useState('0 6 * * *');
  const [tz, setTz] = useState('America/Los_Angeles');
  const [days, setDays] = useState(2);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await api('/api/admin/schedules', {
        method: 'POST',
        body: {
          name,
          scope_states: states.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean),
          cron_expression: cronExpr, timezone: tz, audit_window_days: days, enabled: false,
        },
      });
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'create failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>New schedule slot</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12 }}>
        <Field label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Texas" /></Field>
        <Field label="States"><input className="input mono" value={states} onChange={(e) => setStates(e.target.value)} placeholder="TX" /></Field>
        <Field label="Cron"><input className="input mono" value={cronExpr} onChange={(e) => setCronExpr(e.target.value)} /></Field>
        <Field label="Timezone">
          <select className="select" value={tz} onChange={(e) => setTz(e.target.value)}>
            {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Window (days)">
          <select className="select" value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
            <option value="1">1 (last 24h)</option><option value="2">2 (last 48h)</option>
            <option value="7">7</option><option value="30">30</option><option value="90">90</option>
          </select>
        </Field>
      </div>
      {err && <div className="c-danger" style={{ fontSize: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-sm" disabled={!name.trim() || saving} onClick={save}>{saving ? 'Creating…' : 'Create (disabled)'}</button>
        <button className="btn btn-sm btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
      <div className="muted" style={{ fontSize: 11.5 }}>New slots start disabled — enable once you've watched a "Run now" complete.</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}
